import { Link, useParams } from 'react-router-dom';
import { NEWS_AUTHOR, NEWS_SLUGS, estimateMinutes, newsArticle, newsNeighbours } from '@/content/news';
import { newsMeta } from '@/content/site';
import { formatDate } from '@/lib/format';
import { Card } from '@/components/Card';
import { ButtonLink } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { Prose } from '@/components/Prose';
import { Section } from '@/components/Section';
import { Seo } from '@/components/Seo';

export function NewsPostPage() {
  const { slug = '' } = useParams();
  const meta = newsMeta(slug);
  const article = newsArticle(slug);

  if (!meta || !article) {
    return (
      <>
        <Seo path="/news" title="文章未找到 — Havorix™" description="这篇文章不存在或已被移除。" />
        <Section spacing="loose">
          <div className="mx-auto flex max-w-xl flex-col items-center gap-4 text-center">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-grad-tint text-brand">
              <Icon name="alert" size={22} />
            </span>
            <h1 className="text-2xl font-semibold text-fg">未找到该文章</h1>
            <p className="text-sm leading-relaxed text-fg-muted">
              链接可能有误，或该内容已不在收录范围内。动态页中列有当前全部文章。
            </p>
            <ButtonLink to="/news" variant="primary">
              回到动态
            </ButtonLink>
          </div>
        </Section>
      </>
    );
  }

  const minutes = estimateMinutes(article.blocks);
  const { newer, older } = newsNeighbours(slug, NEWS_SLUGS);
  const newerMeta = newer ? newsMeta(newer) : undefined;
  const olderMeta = older ? newsMeta(older) : undefined;

  return (
    <>
      <Seo
        path={`/news/${slug}`}
        title={`${meta.title} — Havorix™`}
        description={meta.excerpt}
        type="article"
        publishedAt={meta.date}
        tags={meta.tags}
        crumbs={[
          { name: '首页', path: '/' },
          { name: '动态', path: '/news' },
          { name: meta.title, path: `/news/${slug}` },
        ]}
      />

      <Section tone="grid" spacing="tight" className="border-b border-line">
        <div className="max-w-3xl">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-fg-muted">
            <Link to="/news" className="transition-colors hover:text-fg">
              动态
            </Link>
            <Icon name="arrowRight" size={12} className="text-fg-subtle" />
            <span className="metric-value">{formatDate(meta.date)}</span>
          </nav>

          <h1 className="mt-4 text-3xl font-semibold leading-tight text-fg md:text-[2.25rem] md:leading-[1.2]">
            {meta.title}
          </h1>

          <p className="mt-4 text-base leading-relaxed text-fg-muted">{meta.excerpt}</p>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-fg-subtle">
            <span>{NEWS_AUTHOR}</span>
            <span aria-hidden="true">·</span>
            <span className="metric-value">{formatDate(meta.date)}</span>
            <span aria-hidden="true">·</span>
            <span>约 {minutes} 分钟</span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {meta.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-md border border-line-strong bg-surface-1 px-2.5 py-1 text-xs text-fg-muted"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl">
          <Prose blocks={article.blocks} />

          <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-6">
            <p className="text-xs text-fg-subtle">
              文中提到的是平台当前行为，接口与阈值参数以实际部署为准。
            </p>
            <Link
              to="/news"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-fg transition-colors hover:text-accent"
            >
              <Icon name="arrowLeft" size={14} />
              全部动态
            </Link>
          </div>
        </div>
      </Section>

      {newerMeta || olderMeta ? (
        <Section tone="raised">
          <div className="grid gap-4 md:grid-cols-2">
            {newerMeta ? (
              <Card interactive className="flex flex-col gap-2">
                <span className="label-mono">更新的一篇</span>
                <Link to={`/news/${newerMeta.slug}`} className="text-base font-semibold text-fg hover:text-accent">
                  {newerMeta.title}
                </Link>
                <span className="metric-value text-xs text-fg-subtle">{formatDate(newerMeta.date)}</span>
              </Card>
            ) : null}

            {olderMeta ? (
              <Card interactive className="flex flex-col gap-2">
                <span className="label-mono">更早的一篇</span>
                <Link to={`/news/${olderMeta.slug}`} className="text-base font-semibold text-fg hover:text-accent">
                  {olderMeta.title}
                </Link>
                <span className="metric-value text-xs text-fg-subtle">{formatDate(olderMeta.date)}</span>
              </Card>
            ) : null}
          </div>
        </Section>
      ) : null}
    </>
  );
}
